import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { campaignSchema } from './schema'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useConfirmation } from '@/contexts/confirmation'
import { paths } from '@/utils/path'
import {
    bulkDelivery,
    createMailerCampaign,
    getMailerCampaignByID,
    getMailerCampaignReceivers,
    getMailerTemplates,
    sendTestEmail,
    updateMailerCampaign,
} from '../../../services'
import {
    MailerReceiver,
    MailerTemplateVariableItem,
    MailerCampaignSharedVariable,
    UpdateMailerCampaignPayload,
    MailerAttachment,
    MailerInternalMode,
    MailerInternalType,
    getInternalVariablesByType,
} from '../../../types'
import { previewInternalFilters } from '../../../services'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useAuthStore from '@/stores/useAuthStore'
import { Pagination } from '@/types/shared'

export interface FormValues {
    mailer_campaign_name: string
    mailer_campaign_description: string
    mailer_campaign_source_type: 'internal' | 'external'
    mailer_template_id: number | null
    template_subject: string
    template_content: string
    test_email: string
    smtp_enabled: boolean
    sender_enabled: boolean
    // Internal source fields
    mailer_campaign_internal_mode: MailerInternalMode
    mailer_campaign_internal_type: MailerInternalType
    mailer_campaign_filters: {
        customer_status: string[]
        customer_industry: string[]
        product_group_name: string[]
        service_status: string[]
        service_interestings: string[]
        customer_sale_followers: number[]
        excluded_customer_ids: number[]
        required_fields: string[]
        is_vn?: boolean
    }
    metadata: {
        mailer: {
            address: string
            user_name: string
            password: string
        }
        sender: {
            name: string
            gender: string
            phone: string
            position: string
        }
        disable_layout?: boolean
    }
}

export const useCampaignForm = (campaignId: number | null) => {
    const { employee } = useAuthStore()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { setConfirmation } = useConfirmation()

    const isNew = !campaignId

    // Default SMTP username from env
    const defaultSmtpUsername =
        import.meta.env.APP_DEFAULT_SMTP_USERNAME || 'no-reply@vnetwork.vn'

    // Default values with employee info
    const defaultValues: FormValues = useMemo(
        () => ({
            mailer_campaign_name: '',
            mailer_campaign_description: '',
            mailer_campaign_source_type: 'internal',
            mailer_template_id: null,
            template_subject: '',
            template_content: '',
            test_email: employee?.employee_email_company || '',
            smtp_enabled: false,
            sender_enabled: false,
            mailer_campaign_internal_mode: 'filters',
            mailer_campaign_internal_type: 'customers',
            mailer_campaign_filters: {
                customer_status: [],
                customer_industry: [],
                product_group_name: [],
                service_status: [],
                service_interestings: [],
                customer_sale_followers: employee?.employee_id
                    ? [employee.employee_id]
                    : [],
                excluded_customer_ids: [],
                required_fields: [],
                is_vn: true,
            },
            metadata: {
                mailer: {
                    address: 'mail.vnetwork.vn',
                    user_name: defaultSmtpUsername,
                    password: '',
                },
                sender: {
                    name: employee?.employee_name || '',
                    gender: '',
                    phone: employee?.employee_phone_number || '',
                    position: employee?.position_name || '',
                },
                disable_layout: false,
            },
        }),
        [employee]
    )
    const [receivers, setReceivers] = useState<MailerReceiver[]>([])
    const [importedColumns, setImportedColumns] = useState<string[]>([])
    const [selectedSource, setSelectedSource] = useState<string | null>(null)
    const [selectedTemplateVariables, setSelectedTemplateVariables] = useState<
        MailerTemplateVariableItem[]
    >([])
    // Saved variable config from campaign (full variable objects)
    const [savedCustomerVariables, setSavedCustomerVariables] = useState<
        MailerTemplateVariableItem[]
    >([])
    const [savedSharedVariables, setSavedSharedVariables] = useState<
        MailerCampaignSharedVariable[]
    >([])
    // Track attachments from editor
    const [attachments, setAttachments] = useState<MailerAttachment[]>([])

    // Filters preview state
    const [filtersPreview, setFiltersPreview] = useState<{
        receivers: MailerReceiver[]
        pagination?: Pagination
    }>({
        receivers: [],
        pagination: {
            total: 0,
            page: 1,
            page_size: 10,
            total_pages: 0,
            page_next: 0,
            page_prev: 0,
        },
    })
    const [isPreviewingFilters, setIsPreviewingFilters] = useState(false)
    // Pagination state for filters preview
    const [filtersPage, setFiltersPage] = useState(1)
    const [filtersPageSize] = useState(10)
    const [filtersSearch, setFiltersSearch] = useState('')
    // Store total without search (for display - search doesn't affect total count)
    const [filtersTotalWithoutSearch, setFiltersTotalWithoutSearch] =
        useState(0)

    const formMethods = useForm<FormValues>({
        defaultValues,

        resolver: yupResolver(campaignSchema(t)) as any,
    })
    const { control, handleSubmit, watch, reset, setValue } = formMethods
    const watchedValues = watch()

    // Queries
    const {
        data: campaignData,
        isLoading: isLoadingCampaign,
        renderState: renderStateCampaign,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.MAILER_CAMPAIGNS_DETAIL, campaignId],
        queryFn: () => getMailerCampaignByID(campaignId!),
        enabled: !!campaignId,
    })

    const { data: templates, renderState: renderStateTemplates } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.MAILER_TEMPLATES],
            queryFn: () => getMailerTemplates(),
        })

    // Fetch receivers by campaign ID
    const { data: receiversData, isLoading: isLoadingReceivers } =
        useQueryWithHandler({
            queryKey: [
                QUERY_KEYS.MAILER_CAMPAIGNS_DETAIL,
                campaignId,
                'receivers',
            ],
            queryFn: () => getMailerCampaignReceivers(campaignId!),
            enabled:
                !!campaignId &&
                campaignData?.campaign?.mailer_campaign_source_type ===
                    'external',
        })

    // Sync receivers from API to local state
    useEffect(() => {
        if (receiversData && receiversData.length > 0) {
            setReceivers(receiversData)
        }
    }, [receiversData])

    // Reset form when campaign data is loaded
    useEffect(() => {
        if (campaignData?.campaign) {
            const campaign = campaignData.campaign

            console.log('campaignData', campaign)
            // Prioritize template from campaign response, fallback to templates list
            const template =
                campaignData?.template ||
                templates?.results?.find(
                    (t) => t.mailer_template_id === campaign.mailer_template_id
                )

            // Determine internal mode based on campaign data
            const hasFilters = !!campaign.mailer_campaign_filters
            const hasPresetSource = !!campaign.mailer_campaign_source
            const internalMode = hasFilters
                ? 'filters'
                : hasPresetSource
                  ? 'preset'
                  : 'filters'

            // Get filters from campaign
            const filters = campaign.mailer_campaign_filters || {}

            reset({
                mailer_campaign_name: campaign.mailer_campaign_name,
                mailer_campaign_description:
                    campaign.mailer_campaign_description || '',
                mailer_campaign_source_type:
                    campaign.mailer_campaign_source_type,
                mailer_template_id: campaign.mailer_template_id,
                template_subject: template?.mailer_template_subject || '',
                template_content: template?.mailer_template_content || '',
                test_email: '',
                smtp_enabled: !!campaign.metadata?.mailer,
                sender_enabled: !!campaign.metadata?.sender,
                // Include internal fields in reset
                mailer_campaign_internal_mode: internalMode as
                    | 'preset'
                    | 'filters',
                mailer_campaign_internal_type:
                    campaign.mailer_campaign_internal_type || 'customers',
                mailer_campaign_filters: {
                    customer_status: filters.customer_status || [],
                    customer_industry: filters.customer_industry || [],
                    product_group_name: filters.product_group_name || [],
                    service_status: filters.service_status || [],
                    service_interestings: filters.service_interestings || [],
                    customer_sale_followers:
                        filters.customer_sale_followers || [],
                    excluded_customer_ids: filters.excluded_customer_ids || [],
                    required_fields: filters.required_fields || [],
                    is_vn: filters?.is_vn ?? true,
                },
                metadata: {
                    mailer: {
                        address:
                            campaign.metadata?.mailer?.address ||
                            'mail.vnetwork.vn',
                        user_name:
                            campaign.metadata?.mailer?.user_name ||
                            defaultSmtpUsername,
                        password: campaign.metadata?.mailer?.password || '',
                    },
                    sender: {
                        name: campaign.metadata?.sender?.name || '',
                        gender: campaign.metadata?.sender?.gender || '',
                        phone: campaign.metadata?.sender?.phone || '',
                        position: campaign.metadata?.sender?.position || '',
                    },
                    disable_layout: campaign.metadata?.disable_layout || false,
                },
            })

            // Load saved variable config from campaign
            // Priority: campaign saved variables > template variables
            const hasSavedVariables =
                (campaign.mailer_campaign_customer_variables &&
                    campaign.mailer_campaign_customer_variables.length > 0) ||
                (campaign.mailer_campaign_shared_values &&
                    campaign.mailer_campaign_shared_values.length > 0)

            if (hasSavedVariables) {
                // Merge customer + shared variables to get all variables for editor
                const allSavedVariables: MailerTemplateVariableItem[] = [
                    ...(campaign.mailer_campaign_customer_variables || []),
                    ...(campaign.mailer_campaign_shared_values || []).map(
                        (v) => ({
                            name: v.name,
                            label: v.label,
                            description: v.description,
                        })
                    ),
                ]
                setSelectedTemplateVariables(allSavedVariables)
                setSavedCustomerVariables(
                    campaign.mailer_campaign_customer_variables || []
                )
                setSavedSharedVariables(
                    campaign.mailer_campaign_shared_values || []
                )
            } else if (template?.mailer_template_variables) {
                // Fallback to template variables if no saved variables
                setSelectedTemplateVariables(template.mailer_template_variables)
            }

            // Load attachments from template
            if (template?.mailer_template_attachments) {
                setAttachments(template.mailer_template_attachments)
            }

            // Load selected source for internal campaigns (preset mode)
            if (campaign.mailer_campaign_source) {
                setSelectedSource(campaign.mailer_campaign_source)
            }
        }
    }, [campaignData, templates, reset])

    // Set importedColumns and savedCustomerVariables when internal + filters mode
    // Internal vars (email, name, company, phone) -> fixed in receiver section
    useEffect(() => {
        if (
            watchedValues.mailer_campaign_source_type === 'internal' &&
            watchedValues.mailer_campaign_internal_mode === 'filters'
        ) {
            // Get predefined variables for the internal type
            const internalVariables = getInternalVariablesByType(
                watchedValues.mailer_campaign_internal_type
            )

            // Internal variables available from API (email, name, company, phone)
            const internalVariablesWithEmail = [
                {
                    name: 'email',
                    label: 'Email',
                    description: 'Email khách hàng',
                },
                ...internalVariables,
            ]
            const variableNames = internalVariablesWithEmail.map((v) => v.name)
            setImportedColumns(variableNames)

            // Internal vars always go to customer/receiver section (fixed)
            setSavedCustomerVariables(internalVariablesWithEmail)

            // Add internal vars to selectedTemplateVariables if not already present
            // Keep existing template vars (they go to sharings)
            setSelectedTemplateVariables((prev) => {
                const internalNames = internalVariablesWithEmail.map(
                    (v) => v.name
                )
                // Filter out any existing internal vars, then add fresh ones at start
                const templateVarsOnly = prev.filter(
                    (v) => !internalNames.includes(v.name)
                )
                return [...internalVariablesWithEmail, ...templateVarsOnly]
            })
        }
    }, [
        watchedValues.mailer_campaign_source_type,
        watchedValues.mailer_campaign_internal_mode,
        watchedValues.mailer_campaign_internal_type,
    ])

    // Get internal variables with email for internal + filters mode
    const getInternalVariablesWithEmail = useCallback(() => {
        const internalVariables = getInternalVariablesByType(
            watchedValues.mailer_campaign_internal_type
        )
        return [
            {
                name: 'email',
                label: 'Email',
                description: 'Email khách hàng',
            },
            ...internalVariables,
        ]
    }, [watchedValues.mailer_campaign_internal_type])

    // Handle template selection - manual function for onChangeAfterHookForm
    const handleTemplateChange = (templateId: number | null) => {
        if (templateId && templates?.results) {
            const selectedTemplate = templates.results.find(
                (t) => t.mailer_template_id === templateId
            )
            if (selectedTemplate) {
                setValue(
                    'template_subject',
                    selectedTemplate.mailer_template_subject
                )
                setValue(
                    'template_content',
                    selectedTemplate.mailer_template_content
                )

                const templateVars =
                    selectedTemplate.mailer_template_variables || []

                // For internal + filters mode:
                // - Internal vars (email, name, company, phone) -> receiver (fixed)
                // - Template vars -> sharings
                const isInternalFilters =
                    watchedValues.mailer_campaign_source_type === 'internal' &&
                    watchedValues.mailer_campaign_internal_mode === 'filters'

                if (isInternalFilters) {
                    const internalVars = getInternalVariablesWithEmail()
                    // Combine: internal vars + template vars (all available)
                    setSelectedTemplateVariables([
                        ...internalVars,
                        ...templateVars,
                    ])
                    // Only internal vars go to customer/receiver section
                    setSavedCustomerVariables(internalVars)
                    setSavedSharedVariables([])
                } else {
                    setSelectedTemplateVariables(templateVars)
                    // Reset saved variables when template changes (new template = fresh start)
                    setSavedCustomerVariables([])
                    setSavedSharedVariables([])
                }
            }
        }
    }

    // Template options
    const templateOptions = useMemo(() => {
        return (
            templates?.results?.map((t) => ({
                value: t.mailer_template_id,
                label: t.mailer_template_subject,
            })) || []
        )
    }, [templates])

    // Mutations — toasts use module namespace
    const { mutateAsync: updateCampaign, isPending: isUpdating } =
        useMutationWithHandler(
            {
                mutationFn: (data: {
                    id: number
                    payload: UpdateMailerCampaignPayload
                }) => updateMailerCampaign(data.id, data.payload),
            },
            {
                reactQueryKeys: QUERY_KEYS.MAILER_CAMPAIGNS,
                onSuccess: () =>
                    toast.success(
                        t('mailerCampaign:campaign_updated_successfully')
                    ),
                onError: () =>
                    toast.error(t('mailerCampaign:campaign_update_failed')),
            }
        )

    const { mutateAsync: createCampaign, isPending: isCreating } =
        useMutationWithHandler(
            {
                mutationFn: (payload: UpdateMailerCampaignPayload) =>
                    createMailerCampaign(payload),
            },
            {
                reactQueryKeys: QUERY_KEYS.MAILER_CAMPAIGNS,
                onSuccess: () =>
                    toast.success(
                        t('mailerCampaign:campaign_created_successfully')
                    ),
                onError: () =>
                    toast.error(t('mailerCampaign:campaign_create_failed')),
            }
        )

    const { mutateAsync: sendTest, isPending: isSendingTest } =
        useMutationWithHandler(
            {
                mutationFn: (data: {
                    id: number
                    payload: { receiver: string; employee_id: number }
                }) => sendTestEmail(data.id, data.payload),
            },
            {
                reactQueryKeys: QUERY_KEYS.MAILER_CAMPAIGNS,
                onSuccess: () =>
                    toast.success(t('mailerCampaign:test_email_sent')),
                onError: () =>
                    toast.error(t('mailerCampaign:test_email_failed')),
            }
        )

    const { mutateAsync: startBulkDelivery, isPending: isStartingDelivery } =
        useMutationWithHandler(
            { mutationFn: (id: number) => bulkDelivery(id, {}) },
            {
                reactQueryKeys: QUERY_KEYS.MAILER_CAMPAIGNS,
                onSuccess: () =>
                    toast.success(t('mailerCampaign:bulk_delivery_started')),
                onError: () =>
                    toast.error(t('mailerCampaign:bulk_delivery_failed')),
            }
        )

    // Handlers
    const handleReceiversChange = (
        newReceivers: MailerReceiver[],
        columns: string[]
    ) => {
        setReceivers(newReceivers)
        setImportedColumns(columns)
    }

    const handleSourceChange = (sourceId: string | null) => {
        setSelectedSource(sourceId)
    }

    const handleInternalModeChange = (mode: MailerInternalMode) => {
        setValue('mailer_campaign_internal_mode', mode)
        // Reset filters preview when switching modes
        setFiltersPreview({
            receivers: [],
            pagination: {
                total: 0,
                page: 1,
                page_size: 10,
                total_pages: 0,
                page_next: 0,
                page_prev: 0,
            },
        })
        setFiltersPage(1)
        if (mode === 'preset') {
            // Clear filters when switching to preset
            setValue('mailer_campaign_filters', {
                customer_status: [],
                customer_industry: [],
                product_group_name: [],
                service_status: [],
                service_interestings: [],
                customer_sale_followers: [],
                excluded_customer_ids: [],
                required_fields: [],
                is_vn: true,
            })
        } else {
            // Clear selected source when switching to filters
            setSelectedSource(null)
        }
    }

    const handlePreviewFilters = useCallback(
        async (page: number = filtersPage, search?: string) => {
            const internalType = watchedValues.mailer_campaign_internal_type
            const internalFilters = watchedValues.mailer_campaign_filters
            if (!internalType) return
            setIsPreviewingFilters(true)

            // Update search state if provided
            const searchQuery = search !== undefined ? search : filtersSearch
            if (search !== undefined) {
                setFiltersSearch(search)
            }

            try {
                // Clean up filters - only include non-empty arrays
                const cleanFilters: Record<string, unknown> = {}
                if (searchQuery) cleanFilters.q = searchQuery
                if (internalFilters.customer_status?.length)
                    cleanFilters.customer_status =
                        internalFilters.customer_status
                if (internalFilters.customer_industry?.length)
                    cleanFilters.customer_industry =
                        internalFilters.customer_industry
                if (internalFilters.product_group_name?.length)
                    cleanFilters.product_group_name =
                        internalFilters.product_group_name
                if (internalFilters.service_status?.length)
                    cleanFilters.service_status = internalFilters.service_status
                if (internalFilters.service_interestings?.length)
                    cleanFilters.service_interestings =
                        internalFilters.service_interestings
                if (internalFilters.customer_sale_followers?.length)
                    cleanFilters.customer_sale_followers =
                        internalFilters.customer_sale_followers
                if (internalFilters.required_fields?.length)
                    cleanFilters.required_fields =
                        internalFilters.required_fields
                if (internalFilters.is_vn !== undefined)
                    cleanFilters.is_vn = internalFilters.is_vn
                const payload = {
                    internal_type: internalType as 'customers' | 'services',
                    filters: cleanFilters,
                    page,
                    page_size: filtersPageSize,
                }
                console.log('Preview filters payload:', payload)
                const result = await previewInternalFilters(payload)
                console.log('Preview filters result:', result)
                setFiltersPreview({
                    receivers: result.results,
                    pagination: result.pagination,
                })
                setFiltersPage(page)
                // Only update total without search when there's no search query
                // This keeps the "real" total for display
                if (!searchQuery) {
                    setFiltersTotalWithoutSearch(result.pagination?.total ?? 0)
                }
            } catch (error) {
                console.error('Preview filters error:', error)
                toast.error(t('mailerCampaign:preview_filters_failed'))
            } finally {
                setIsPreviewingFilters(false)
            }
        },
        [
            watchedValues.mailer_campaign_internal_type,
            watchedValues.mailer_campaign_filters,
            filtersPage,
            filtersPageSize,
            filtersSearch,
            t,
        ]
    )

    // Auto-preview filters when they change (debounced) - reset to page 1
    const previewTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const prevFiltersRef = useRef(watchedValues.mailer_campaign_filters)
    // Tracks whether the initial preview has run. Guards against the effect
    // re-fetching on pure page changes (pagination fetches its own page) or on
    // exclusion toggles — both re-run this effect but need no auto-preview.
    const hasPreviewedRef = useRef(false)
    useEffect(() => {
        // Only auto-preview for internal + filters mode
        if (
            watchedValues.mailer_campaign_source_type !== 'internal' ||
            watchedValues.mailer_campaign_internal_mode !== 'filters'
        ) {
            return
        }

        // Check if at least one filter is set (excluding excluded_customer_ids and required_fields)
        const filters = watchedValues.mailer_campaign_filters
        const hasFilters =
            filters.customer_status.length > 0 ||
            filters.customer_industry.length > 0 ||
            filters.product_group_name.length > 0 ||
            filters.service_status.length > 0 ||
            filters.service_interestings.length > 0 ||
            filters.customer_sale_followers.length > 0 ||
            filters.is_vn !== undefined

        if (!hasFilters) {
            setFiltersPreview({
                receivers: [],
                pagination: undefined,
            })
            return
        }

        // Check if filters changed (excluding excluded_customer_ids and required_fields) to reset page
        const prevFilters = prevFiltersRef.current
        const filtersChanged =
            JSON.stringify({
                ...filters,
                excluded_customer_ids: [],
                required_fields: [],
            }) !==
            JSON.stringify({
                ...prevFilters,
                excluded_customer_ids: [],
                required_fields: [],
            })
        prevFiltersRef.current = filters

        // When filters change, clear stale exclusions — exclusions only make
        // sense within a single filter set; carrying them across filter changes
        // causes counter mismatch (excluded count > 0 while all visible rows
        // appear checked/included).
        // Guard with hasPreviewedRef: on initial hydration (loading a saved
        // campaign) filtersChanged is always true because prevFiltersRef still
        // holds the empty mount defaults. Without this guard the saved
        // excluded_customer_ids get wiped on every reload — and then persisted
        // as [] on the next save, silently losing the exclusions.
        if (
            hasPreviewedRef.current &&
            filtersChanged &&
            (filters.excluded_customer_ids?.length || 0) > 0
        ) {
            setValue('mailer_campaign_filters.excluded_customer_ids', [])
        }

        // Only auto-preview when filters actually changed (reset to page 1) or
        // on the first preview. Pure page changes are handled by the pagination
        // handler and exclusion toggles need no refetch — skip them to avoid a
        // duplicate request (double loading spinner).
        const shouldPreview = filtersChanged || !hasPreviewedRef.current
        if (shouldPreview) {
            // Debounce the preview call
            if (previewTimeoutRef.current) {
                clearTimeout(previewTimeoutRef.current)
            }
            previewTimeoutRef.current = setTimeout(() => {
                hasPreviewedRef.current = true
                // Reset to page 1 if filters changed
                handlePreviewFilters(filtersChanged ? 1 : filtersPage)
            }, 500)
        }

        return () => {
            if (previewTimeoutRef.current) {
                clearTimeout(previewTimeoutRef.current)
            }
        }
    }, [
        watchedValues.mailer_campaign_source_type,
        watchedValues.mailer_campaign_internal_mode,
        watchedValues.mailer_campaign_filters,
        handlePreviewFilters,
        filtersPage,
    ])

    const onSubmit = async (
        data: FormValues,
        variableData?: {
            customerVariables: MailerTemplateVariableItem[]
            sharedVariables: MailerCampaignSharedVariable[]
        },
        templateAttachments?: MailerAttachment[]
    ) => {
        // Custom validation (not handled by yup schema)
        if (data.mailer_campaign_source_type === 'external') {
            if (receivers.length === 0) {
                toast.error(t('mailerCampaign:receivers_required'))
                return
            }
        } else if (data.mailer_campaign_source_type === 'internal') {
            if (data.mailer_campaign_internal_mode === 'preset') {
                if (!selectedSource) {
                    toast.error(t('mailerCampaign:internal_source_required'))
                    return
                }
            } else if (data.mailer_campaign_internal_mode === 'filters') {
                // At least one filter must be set
                const hasFilters =
                    data.mailer_campaign_filters.customer_status.length > 0 ||
                    data.mailer_campaign_filters.customer_industry.length > 0 ||
                    data.mailer_campaign_filters.product_group_name.length >
                        0 ||
                    data.mailer_campaign_filters.service_status.length > 0 ||
                    data.mailer_campaign_filters.service_interestings.length >
                        0 ||
                    data.mailer_campaign_filters.customer_sale_followers
                        .length > 0

                if (!hasFilters) {
                    toast.error(t('mailerCampaign:filters_required'))
                    return
                }
            }
        }

        // Debug: log form data
        console.log('=== FORM SUBMIT DEBUG ===')
        console.log('Full form data:', JSON.stringify(data, null, 2))
        console.log('mailer_campaign_filters:', data.mailer_campaign_filters)
        console.log(
            'customer_status:',
            data.mailer_campaign_filters?.customer_status
        )
        console.log(
            'customer_industry:',
            data.mailer_campaign_filters?.customer_industry
        )
        console.log(
            'customer_sale_followers:',
            data.mailer_campaign_filters?.customer_sale_followers
        )

        // Build internal source fields based on mode
        const internalSourceFields =
            data.mailer_campaign_source_type === 'internal'
                ? {
                      mailer_campaign_internal_type:
                          data.mailer_campaign_internal_type,
                      ...(data.mailer_campaign_internal_mode === 'preset'
                          ? {
                                mailer_campaign_source:
                                    selectedSource || undefined,
                            }
                          : {
                                mailer_campaign_filters:
                                    data.mailer_campaign_filters,
                            }),
                  }
                : {}

        const payload: UpdateMailerCampaignPayload = {
            campaign: {
                mailer_campaign_name: data.mailer_campaign_name,
                mailer_campaign_description: data.mailer_campaign_description,
                mailer_campaign_source_type: data.mailer_campaign_source_type,
                ...internalSourceFields,
                mailer_template_id: data.mailer_template_id || undefined,
                mailer_campaign_customer_variables:
                    variableData?.customerVariables,
                mailer_campaign_shared_values: variableData?.sharedVariables,
                metadata: {
                    ...(data.smtp_enabled && {
                        mailer: data.metadata.mailer,
                    }),
                    ...(data.sender_enabled && {
                        sender: data.metadata.sender,
                    }),
                    // Detect if content is full HTML document (disable_layout mode)
                    disable_layout: (() => {
                        const content =
                            data.template_content?.trim().toLowerCase() || ''
                        return (
                            content.startsWith('<!doctype') ||
                            content.startsWith('<html') ||
                            (content.includes('<head') &&
                                content.includes('<body'))
                        )
                    })(),
                },
            },
            receivers:
                data.mailer_campaign_source_type === 'external'
                    ? receivers
                    : undefined,
            // Include template if there's content (even without template_id for imported HTML)
            template:
                data.template_content || data.mailer_template_id
                    ? {
                          mailer_template_id: data.mailer_template_id || 0,
                          mailer_template_subject: data.template_subject,
                          mailer_template_content: data.template_content,
                          mailer_template_attachments: templateAttachments,
                      }
                    : undefined,
            re_upload: receivers.length > 0,
        }

        console.log('=== PAYLOAD DEBUG ===')
        console.log('Payload:', JSON.stringify(payload, null, 2))

        // toasts shown by mutation hook; try/catch absorbs rejection so
        // post-success side effects (navigate, invalidate) skip on failure
        try {
            if (isNew) {
                const response = await createCampaign(payload)
                queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.MAILER_CAMPAIGNS],
                })
                if (response?.result?.mailer_campaign_id) {
                    navigate(
                        paths.mailerCampaignDetail.replace(
                            ':id',
                            String(response.result.mailer_campaign_id)
                        )
                    )
                }
            } else if (campaignId) {
                await updateCampaign({ id: campaignId, payload })
                queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.MAILER_CAMPAIGNS],
                })
            }
        } catch {
            // error toast shown by mutation hook
        }
    }

    const handleSendTest = async () => {
        if (!campaignId || !watchedValues.test_email) return
        // toasts shown by mutation hook
        try {
            await sendTest({
                id: campaignId,
                payload: {
                    receiver: watchedValues.test_email,
                    employee_id: employee?.employee_id || 0,
                },
            })
        } catch {
            // error toast shown by hook
        }
    }

    const handleBulkDelivery = (onSuccess?: () => void) => {
        if (!campaignId) return

        // Get effective receivers count based on source type
        const isInternalFilters =
            watchedValues.mailer_campaign_source_type === 'internal' &&
            watchedValues.mailer_campaign_internal_mode === 'filters'
        const effectiveCount = isInternalFilters
            ? (filtersPreview?.pagination?.total ?? 0) -
              (watchedValues.mailer_campaign_filters.excluded_customer_ids
                  ?.length ?? 0)
            : receivers.length

        setConfirmation({
            title: t('mailerCampaign:start_bulk_delivery'),
            description: t('mailerCampaign:start_bulk_delivery_confirm', {
                count: effectiveCount,
            }),
            onConfirm: async () => {
                // toasts shown by mutation hook; catch gates post-success actions
                try {
                    await startBulkDelivery(campaignId)
                    queryClient.invalidateQueries({
                        queryKey: [
                            QUERY_KEYS.MAILER_CAMPAIGNS_DETAIL,
                            campaignId,
                        ],
                    })
                    onSuccess?.()
                } catch {
                    // error toast shown by hook
                }
            },
            open: true,
        })
    }

    const campaign = campaignData?.campaign
    const canStartDelivery =
        campaign?.mailer_campaign_status === 'draft' && receivers.length > 0
    const currentUserEmail = employee?.employee_email_company || ''

    // Handler to toggle exclude customer
    const handleToggleExcludeCustomer = useCallback(
        (customerId: number) => {
            const currentExcluded =
                watchedValues.mailer_campaign_filters.excluded_customer_ids ||
                []
            const isExcluded = currentExcluded.includes(customerId)

            if (isExcluded) {
                // Remove from excluded
                setValue(
                    'mailer_campaign_filters.excluded_customer_ids',
                    currentExcluded.filter((id) => id !== customerId)
                )
            } else {
                // Add to excluded
                setValue('mailer_campaign_filters.excluded_customer_ids', [
                    ...currentExcluded,
                    customerId,
                ])
            }
        },
        [watchedValues.mailer_campaign_filters.excluded_customer_ids, setValue]
    )

    // Handler to change page
    const handleFiltersPageChange = useCallback(
        (page: number) => {
            handlePreviewFilters(page)
        },
        [handlePreviewFilters]
    )

    return {
        formMethods,
        control,
        handleSubmit,
        watchedValues,
        isNew,
        isLoadingCampaign,
        renderStateCampaign,
        renderStateTemplates,
        campaign,
        campaignId,
        canStartDelivery,
        receivers,
        isLoadingReceivers,
        importedColumns,
        selectedTemplateVariables,
        templateOptions,
        currentUserEmail,
        isCreating,
        isUpdating,
        isSendingTest,
        isStartingDelivery,
        // Saved variable config from campaign (for initializing useVariableManagement)
        savedCustomerVariables,
        savedSharedVariables,
        // Attachments
        attachments,
        setAttachments,
        // Internal source
        selectedSource,
        handleSourceChange,
        // Internal mode and filters (from form)
        filtersPreview,
        isPreviewingFilters,
        filtersPage,
        filtersPageSize,
        filtersTotalWithoutSearch,
        handleInternalModeChange,
        handlePreviewFilters,
        handleFiltersPageChange,
        handleToggleExcludeCustomer,
        handleReceiversChange,
        handleTemplateChange,
        onSubmit,
        handleSendTest,
        handleBulkDelivery,
        navigate,
        templates: templates?.results || [],
    }
}
